<div class="runtime-settings__scroll">
  <label
    class="runtime-settings__field runtime-settings__debug-toggle"
    for="showViewerLoading"
    title="Show the request loading indicator in the viewer."
  >
    <span>Show request loading (Debug)</span>
    <input
      id="showViewerLoading"
      type="checkbox"
      aria-description="Show the request loading indicator in the viewer."
      [checked]="viewer.showViewerLoadingDraft"
      (change)="viewer.onShowViewerLoadingChanged($event)"
    />
  </label>
  <label
    class="runtime-settings__field runtime-settings__debug-toggle"
    for="viewerProfiling"
    title="Collect opt-in render pipeline timing for DevTools and the Performance panel."
  >
    <span>Enable profiling (Debug)</span>
    <input
      id="viewerProfiling"
      type="checkbox"
      aria-description="Collect opt-in render pipeline timing for DevTools and the Performance panel."
      [checked]="viewer.viewerProfilingEnabledDraft"
      (change)="viewer.onViewerProfilingEnabledChanged($event)"
    />
  </label>
  <label
    class="runtime-settings__field runtime-settings__debug-toggle"
    for="viewerDeepProfiling"
    title="Request asynchronous WebGL GPU timer queries when supported."
  >
    <span>Deep GPU profiling</span>
    <input
      id="viewerDeepProfiling"
      type="checkbox"
      aria-description="Request asynchronous WebGL GPU timer queries when supported."
      [checked]="viewer.viewerDeepProfilingEnabledDraft"
      [disabled]="!viewer.viewerProfilingEnabledDraft"
      (change)="viewer.onViewerDeepProfilingEnabledChanged($event)"
    />
  </label>
  <div class="runtime-settings__groups">
    <fieldset *ngFor="let group of viewer.runtimeGroups" [disabled]="viewer.runtimeSettingsBusy">
      <legend>{{ group }}</legend>
      <label
        *ngIf="group === 'Render pipeline'"
        class="runtime-settings__field"
        title="Draws the scene over multiple frames to keep interaction responsive. Camera or scene changes restart drawing."
      >
        <span>Distributed rendering</span>
        <input
          type="checkbox"
          aria-description="Draws the scene over multiple frames to keep interaction responsive. Camera or scene changes restart drawing."
          [checked]="viewer.distributedRenderingDraft"
          (change)="viewer.onDistributedRenderingChanged($event)"
        />
      </label>
      <label
        *ngIf="group === 'Render pipeline'"
        class="runtime-settings__field"
        for="display-downsample"
        title="Renders the scene at a lower internal resolution to reduce GPU pixel workload."
      >
        <span>Downsampling: {{ viewer.sceneStats.downsampleRate }}x</span>
        <input
          type="range"
          id="display-downsample"
          #displayDownsample
          [min]="viewer.downsampleMin"
          [max]="viewer.downsampleMax"
          [value]="viewer.sceneStats.downsampleRate"
          (input)="viewer.setDownsample(displayDownsample.value)"
        />
      </label>
      <label *ngIf="group === 'Render pipeline'" class="runtime-settings__field" for="renderOrderStrategy">
        <span>Object sort strategy</span>
        <select
          id="renderOrderStrategy"
          [value]="viewer.renderOrderStrategyDraft"
          (change)="viewer.onRenderOrderStrategyChanged($event)"
        >
          <option
            *ngFor="let strategy of viewer.renderOrderStrategies"
            [value]="strategy.value"
            [selected]="strategy.value === viewer.renderOrderStrategyDraft"
            [title]="strategy.description"
          >
            {{ strategy.label }}
          </option>
        </select>
      </label>
      <label
        *ngIf="group === 'Render pipeline'"
        class="runtime-settings__field"
        for="navigationMinScreenSize"
        title="Skips small loaded meshes, lines and point-cloud blocks only during navigation. Uses CSS pixels; restores full detail when navigation stops."
      >
        <span>Hide objects below size during navigation (px)</span>
        <select
          id="navigationMinScreenSize"
          [value]="viewer.navigationMinScreenSizeDraft"
          (change)="viewer.onNavigationMinScreenSizeChanged($event)"
        >
          <option
            *ngFor="let option of viewer.navigationMinScreenSizes"
            [value]="option.value"
            [selected]="option.value === viewer.navigationMinScreenSizeDraft"
          >
            {{ option.label }}
          </option>
        </select>
      </label>
      <label *ngIf="group === 'Render pipeline'" class="runtime-settings__field" for="geometryFormat">
        <span>Mesh format</span>
        <select
          id="geometryFormat"
          [value]="viewer.geometryFormatDraft"
          (change)="viewer.onGeometryFormatChanged($event)"
        >
          <option
            *ngFor="let format of viewer.geometryFormats"
            [value]="format.value"
            [selected]="format.value === viewer.geometryFormatDraft"
            [title]="format.description"
          >
            {{ format.label }}
          </option>
        </select>
      </label>
      <label *ngIf="group === 'Frustum'" class="runtime-settings__field" for="frustumResponseFormat">
        <span>Frustum response format</span>
        <select
          id="frustumResponseFormat"
          [value]="viewer.frustumResponseFormatDraft"
          (change)="viewer.onFrustumResponseFormatChanged($event)"
        >
          <option
            *ngFor="let format of viewer.frustumResponseFormats"
            [value]="format.value"
            [selected]="format.value === viewer.frustumResponseFormatDraft"
            [title]="format.description"
          >
            {{ format.label }}
          </option>
        </select>
      </label>
      <label
        *ngIf="group === 'Frustum'"
        class="runtime-settings__field"
        for="hlodOcclusionRefinement"
        title="Refine only HLOD nodes that are not hidden behind other nodes, checked with a low-resolution visibility pass. Hidden nodes keep their current level."
      >
        <span>HLOD occlusion-aware refinement</span>
        <input
          id="hlodOcclusionRefinement"
          type="checkbox"
          [checked]="viewer.hlodOcclusionRefinementDraft"
          (change)="viewer.onHlodOcclusionRefinementChanged($event)"
        />
      </label>
      <label
        *ngIf="group === 'Render pipeline'"
        class="runtime-settings__field"
        for="downloadBatchAuto"
        title="Automatically size Draco mesh batches using the target MB and minimum geometry count."
      >
        <span>Mesh download batch auto</span>
        <input
          id="downloadBatchAuto"
          type="checkbox"
          [checked]="viewer.downloadBatchAutoDraft"
          (change)="viewer.onDownloadBatchAutoChanged($event)"
        />
      </label>
      <label *ngIf="group === 'Render pipeline'" class="runtime-settings__field" for="pointCloudGeometryFormat">
        <span>Point-cloud binary format</span>
        <select
          id="pointCloudGeometryFormat"
          [value]="viewer.pointCloudGeometryFormatDraft"
          (change)="viewer.onPointCloudGeometryFormatChanged($event)"
        >
          <option
            *ngFor="let format of viewer.pointCloudGeometryFormats"
            [value]="format.value"
            [selected]="format.value === viewer.pointCloudGeometryFormatDraft"
            [title]="format.description"
          >
            {{ format.label }}
          </option>
        </select>
      </label>
      <label *ngIf="group === 'Render pipeline'" class="runtime-settings__field" for="disableTransparentMaterials">
        <span>Disable transparent materials</span>
        <input
          id="disableTransparentMaterials"
          type="checkbox"
          [checked]="viewer.disableTransparentMaterialsDraft"
          (change)="viewer.onDisableTransparentMaterialsChanged($event)"
        />
      </label>
      <label
        *ngIf="group === 'Render pipeline'"
        class="runtime-settings__field"
        for="enableInstancing"
        title="Shares mesh buffers, instances adjacent transparent meshes in order, and batches small opaque meshes with multi-draw when supported."
      >
        <span>Enable mesh batching / instancing</span>
        <input
          id="enableInstancing"
          type="checkbox"
          aria-description="Shares mesh buffers, instances adjacent transparent meshes in order, and batches small opaque meshes with multi-draw when supported."
          [checked]="viewer.enableInstancingDraft"
          (change)="viewer.onInstancingChanged($event)"
        />
      </label>
      <label
        *ngIf="group === 'Render pipeline'"
        class="runtime-settings__field"
        for="pointCloudAdaptivePointSize"
        title="Sizes point cloud points for the spacing drawn over each block, from the point size up to 2.5 times it, so sparse regions stay closed."
      >
        <span>Adaptive point size</span>
        <input
          id="pointCloudAdaptivePointSize"
          type="checkbox"
          aria-description="Sizes point cloud points for the spacing drawn over each block, from the point size up to 2.5 times it, so sparse regions stay closed."
          [checked]="viewer.pointCloudAdaptivePointSizeDraft"
          (change)="viewer.onPointCloudAdaptivePointSizeChanged($event)"
        />
      </label>
      <label *ngIf="group === 'Frustum'" class="runtime-settings__field" for="meshFrustumSortOrder">
        <span>Mesh frustum sort</span>
        <select
          id="meshFrustumSortOrder"
          [value]="viewer.meshFrustumSortOrderDraft"
          (change)="viewer.onFrustumSortOrderChanged($event)"
        >
          <option
            *ngFor="let sortOrder of viewer.frustumSortOrders"
            [value]="sortOrder.value"
            [selected]="sortOrder.value === viewer.meshFrustumSortOrderDraft"
            [title]="sortOrder.description"
          >
            {{ sortOrder.label }}
          </option>
        </select>
      </label>
      <label *ngIf="group === 'Frustum'" class="runtime-settings__field" for="freezeFrustum">
        <span>Fix frustum</span>
        <input
          id="freezeFrustum"
          type="checkbox"
          title="Freeze server geometry updates. Local camera culling and rendering remain active."
          aria-description="Freeze server geometry updates. Local camera culling and rendering remain active."
          [checked]="viewer.freezeFrustumDraft"
          (change)="viewer.onFreezeFrustumChanged($event)"
        />
      </label>
      <label *ngIf="group === 'Frustum'" class="runtime-settings__field" for="showPointCloudBoxes">
        <span>View point-cloud boxes</span>
        <input
          id="showPointCloudBoxes"
          type="checkbox"
          title="Draw a wireframe box around every loaded point-cloud block."
          aria-description="Draw a wireframe box around every loaded point-cloud block."
          [checked]="viewer.showPointCloudBoxesDraft"
          (change)="viewer.onShowPointCloudBoxesChanged($event)"
        />
      </label>
      <label *ngIf="group === 'Frustum'" class="runtime-settings__field" for="showObjectBoundingBoxes">
        <span>View object bounding boxes</span>
        <input
          id="showObjectBoundingBoxes"
          type="checkbox"
          title="Draw a magenta wireframe box around every loaded mesh or line object."
          aria-description="Draw a magenta wireframe box around every loaded mesh or line object."
          [checked]="viewer.showObjectBoundingBoxesDraft"
          (change)="viewer.onShowObjectBoundingBoxesChanged($event)"
        />
      </label>
      <label
        *ngIf="group === 'Camera'"
        class="runtime-settings__field"
        for="adaptiveNavigationWheel"
        title="Adjust wheel navigation speed from the distance to the object under the cursor."
      >
        <span>Adaptive navigation (Wheel)</span>
        <input
          id="adaptiveNavigationWheel"
          type="checkbox"
          aria-description="Adjust wheel navigation speed from the distance to the object under the cursor."
          [checked]="viewer.adaptiveNavigationWheelDraft"
          (change)="viewer.onAdaptiveNavigationWheelChanged($event)"
        />
      </label>
      <label
        *ngIf="group === 'Camera'"
        class="runtime-settings__field"
        for="adaptiveNavigationKeyboard"
        title="Adjust keyboard navigation speed from the distance to the object under the cursor."
      >
        <span>Adaptive navigation (Keyboard)</span>
        <input
          id="adaptiveNavigationKeyboard"
          type="checkbox"
          aria-description="Adjust keyboard navigation speed from the distance to the object under the cursor."
          [checked]="viewer.adaptiveNavigationKeyboardDraft"
          (change)="viewer.onAdaptiveNavigationKeyboardChanged($event)"
        />
      </label>
      <ng-container *ngFor="let field of viewer.runtimeFields">
        <label
          *ngIf="field.group === group"
          class="runtime-settings__field"
          [for]="'runtime-' + field.key"
          [title]="field.description"
        >
          <span>{{ field.label }}</span>
          <input
            [id]="'runtime-' + field.key"
            [title]="field.description"
            [attr.aria-description]="field.description"
            type="number"
            [min]="viewer.getRuntimeFieldInputMin(field)"
            [max]="viewer.getRuntimeFieldInputMax(field)"
            [step]="viewer.getRuntimeFieldInputStep(field)"
            [value]="viewer.getRuntimeFieldInputValue(field)"
            [disabled]="field.key === 'downloadMinBatchBytes' && !viewer.downloadBatchAutoDraft"
            (input)="viewer.onRuntimeSettingChanged(field.key, $event)"
          />
        </label>
      </ng-container>
    </fieldset>
    <fieldset>
      <legend>Client cache</legend>
      <label
        class="runtime-settings__field"
        for="photogrammetryCacheEnabled"
        title="Keep photogrammetry tiles (manifests, subtrees, node payloads) in this browser's IndexedDB, so reopening a scene downloads only what changed. Up to 3 GiB on desktop, 1 GiB on phones, at most half of the storage quota."
      >
        <span>Keep photogrammetry tiles on this device</span>
        <input
          id="photogrammetryCacheEnabled"
          type="checkbox"
          [checked]="viewer.photogrammetryCacheEnabled"
          (change)="viewer.onPhotogrammetryCacheEnabledChanged($event)"
        />
      </label>
      <label
        class="runtime-settings__field"
        for="geometryCacheEnabled"
        title="Keep downloaded scene meshes and lines in this browser's IndexedDB; point clouds are never kept. Same budget as the tiles."
      >
        <span>Keep scene geometry on this device</span>
        <input
          id="geometryCacheEnabled"
          type="checkbox"
          [checked]="viewer.geometryCacheEnabled"
          (change)="viewer.onGeometryCacheEnabledChanged($event)"
        />
      </label>
      <div class="runtime-settings__field runtime-settings__field--actions">
        <button type="button" [disabled]="viewer.clientCacheBusy" (click)="viewer.clearClientCaches()">
          Clear caches
        </button>
        <button type="button" [disabled]="viewer.clientCacheBusy" (click)="viewer.refreshClientCacheUsage()">
          Refresh
        </button>
      </div>
      <p class="runtime-settings__note" role="status">
        {{ viewer.clientCacheMessage }} {{ viewer.clientCacheUsageMessage }}
      </p>
    </fieldset>
  </div>
  <div *ngIf="viewer.runtimeSettingsError" class="ingest-panel__error" role="alert">
    {{ viewer.runtimeSettingsError }}
  </div>
</div>
<div class="viewer-panel__actions">
  <button
    type="button"
    [disabled]="viewer.runtimeSettingsBusy || viewer.runtimeSettingsError !== null"
    (click)="viewer.applyRuntimeSettingsNow()"
  >
    Apply
  </button>
  <button type="button" [disabled]="viewer.runtimeSettingsBusy" (click)="viewer.resetRuntimeSettingsDraft()">
    Reset to Defaults
  </button>
  <button
    type="button"
    *ngIf="viewer.selectedSceneId !== null"
    [disabled]="viewer.runtimeSettingsBusy"
    (click)="viewer.copyViewerUrlToClipboard()"
  >
    Copy URL with camera
  </button>
  <ng-container *ngIf="viewer.settingsApplyCountdown !== null; else settingsStatus">
    <span role="status" class="viewer-panel__apply-countdown"
      >Changes will be applied in {{ viewer.settingsApplyCountdown }} seconds</span
    >
    <button type="button" (click)="viewer.cancelPendingSettings()">Cancel</button>
  </ng-container>
  <ng-template #settingsStatus>
    <span role="status">{{
      viewer.runtimeSettingsBusy ? 'Loading scene...' : viewer.settingsApplyPending ? 'Applying changes...' : viewer.runtimeSettingsMessage
    }}</span>
  </ng-template>
</div>
